import {
  ActionIcon,
  Box,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconLayoutSidebarRightExpand } from '@tabler/icons-react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getZoomSignature } from '@/lib/zoom/signature';
import { actorRefIsAppointmentParticipant } from '@/features/calendar/calendarEventGuards';
import { resolveCurrentUserPractitioner } from '@/lib/practitionerIdentity';
import { ZOOM_OVERLAY_CHROME_Z_INDEX, ZOOM_OVERLAY_COMBOBOX_Z_INDEX, ZOOM_OVERLAY_MODAL_Z_INDEX, ZOOM_SDK_Z_INDEX } from './lib/sessionAttendance';
import { SessionClinicalPanelContent } from './components/SessionClinicalPanelContent';
import { useZoomSessionData } from './hooks/useZoomSessionData';
import {
  clearZoomCoiRetryMarker,
  decideZoomCoiAction,
  readZoomCoiRetryMarker,
  writeZoomCoiRetryMarker,
} from './lib/zoomCoiIsolation';

const PANEL_WIDTH = 520;
const CHROME_HEADER_HEIGHT = 40;
const PANEL_INSET = 8;
const ZOOM_CHROME_Z_INDEX = ZOOM_OVERLAY_CHROME_Z_INDEX;
const EDGE_CONTROL_SIZE = 44;

type ZoomIsolationStatus = 'pending' | 'ready' | 'failed';

type ZoomClient = typeof import('@zoom/meetingsdk').ZoomMtg;

/**
 * True once this document has already requested a COI reload. Module-scoped so
 * a StrictMode unmount/remount of the effect cannot re-decide (and flash the
 * failure UI) while the reload navigation is in flight.
 */
let coiReloadRequestedInThisDocument = false;

export function ZoomCallPage(): JSX.Element {
  // Reading crossOriginIsolated is pure; all storage/reload side effects live in the effect below.
  const [isolationStatus, setIsolationStatus] = useState<ZoomIsolationStatus>(() =>
    window.crossOriginIsolated ? 'ready' : 'pending',
  );

  useEffect(() => {
    if (isolationStatus === 'ready') {
      clearZoomCoiRetryMarker(sessionStorage);
      return;
    }
    if (isolationStatus !== 'pending' || coiReloadRequestedInThisDocument) {
      return;
    }
    const action = decideZoomCoiAction({
      crossOriginIsolated: window.crossOriginIsolated,
      alreadyRetried: readZoomCoiRetryMarker(sessionStorage),
    });
    if (action.kind === 'proceed') {
      setIsolationStatus('ready');
      return;
    }
    if (action.kind === 'reload' && writeZoomCoiRetryMarker(sessionStorage)) {
      coiReloadRequestedInThisDocument = true;
      window.location.reload();
      return;
    }
    // 'fail', or the retry marker could not be persisted (fail closed, never loop).
    setIsolationStatus('failed');
  }, [isolationStatus]);

  if (isolationStatus === 'pending') {
    // Waiting on the one-shot reload decision; render nothing so no Zoom/network work starts.
    return <></>;
  }

  if (isolationStatus === 'failed') {
    return (
      <Text c="red" ta="center" mt="xl" maw={480} mx="auto" px="md">
        This browser session could not enable the features required for the video call view.
        Close this tab and join again from Unify, or try Chrome or Edge.
      </Text>
    );
  }

  return <ZoomCallPageContent />;
}

function ZoomCallPageContent(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [panelOpen, setPanelOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const flushRef = useRef<(() => Promise<void>) | null>(null);
  const openButtonRef = useRef<HTMLButtonElement | null>(null);
  const closingRef = useRef(false);

  const urlParams = new URLSearchParams(window.location.search);
  const appointmentId = urlParams.get('appointmentId') ?? undefined;
  const isClientJoin = urlParams.get('clientJoin') === '1';
  const [joinAllowed, setJoinAllowed] = useState<boolean | null>(isClientJoin ? true : null);
  // Kept for its loading/autosave side effects — the client join path has no panel instance.
  useZoomSessionData(appointmentId, { loadClinicalData: !isClientJoin });

  useEffect(() => {
    const root = document.getElementById('root');
    const zmmtg = document.getElementById('zmmtg-root');
    document.body.classList.add('zoom-meeting-active');

    if (root) {
      root.style.pointerEvents = 'none';
    }
    if (zmmtg) {
      zmmtg.style.position = 'fixed';
      zmmtg.style.inset = '0';
      zmmtg.style.zIndex = String(ZOOM_SDK_Z_INDEX);
      zmmtg.style.pointerEvents = 'auto';
    }

    return () => {
      document.body.classList.remove('zoom-meeting-active');
      if (root) {
        root.style.pointerEvents = '';
      }
      if (zmmtg) {
        zmmtg.style.position = '';
        zmmtg.style.inset = '';
        zmmtg.style.zIndex = '';
        zmmtg.style.pointerEvents = '';
      }
    };
  }, []);

  useEffect(() => {
    if (isClientJoin) {
      setJoinAllowed(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      if (!appointmentId) {
        if (!cancelled) setJoinAllowed(false);
        return;
      }
      try {
        const appointment = await medplum.readResource('Appointment', appointmentId);
        const practitioner = await resolveCurrentUserPractitioner(medplum, profile);
        const allowed = actorRefIsAppointmentParticipant(
          appointment.participant,
          practitioner?.id ? `Practitioner/${practitioner.id}` : undefined,
        );
        if (!cancelled) setJoinAllowed(allowed);
      } catch {
        if (!cancelled) setJoinAllowed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appointmentId, isClientJoin, medplum, profile]);

  useEffect(() => {
    let zoomClient: ZoomClient | null = null;
    let cancelled = false;

    const params = new URLSearchParams(window.location.search);
    const meetingNumber = params.get('zoomMeetingNumber') ?? '';
    const password = params.get('zoomPassword') ?? '';
    const userName = params.get('zoomUserName') ?? 'Provider';
    const userEmail = params.get('zoomUserEmail') ?? '';
    const role = Number(params.get('zoomRole') ?? '1');
    const clientJoin = params.get('clientJoin') === '1';

    if (joinAllowed !== true) {
      return;
    }

    if (!meetingNumber) {
      setError('No meeting number provided');
      return;
    }

    void startMeeting(medplum, {
      meetingNumber,
      password,
      userName,
      userEmail,
      role,
      clientJoin,
    })
      .then((joinedClient) => {
        zoomClient = joinedClient;
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[Zoom] Error joining meeting:', err);
        setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
      if (zoomClient) {
        void leaveZoomMeeting(zoomClient);
      }
    };
  }, [medplum, joinAllowed]);

  const openPanel = useCallback(() => setPanelOpen(true), []);

  const closePanel = useCallback(async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    try {
      await flushRef.current?.();
    } finally {
      setPanelOpen(false);
      closingRef.current = false;
      requestAnimationFrame(() => {
        openButtonRef.current?.focus();
      });
    }
  }, []);

  const registerFlush = useCallback((flush: (() => Promise<void>) | null) => {
    flushRef.current = flush;
  }, []);

  // No Unify top bar: Zoom's native chrome (including the top-right View menu) stays
  // fully visible — ENG-3306. Only the clinical drawer + its edge button remain.
  const overlay = isClientJoin ? null : createPortal(
    <Box id="unify-zoom-clinical-chrome" style={{ pointerEvents: 'none' }}>
      {/* Keep mounted while collapsed so tab state and loaded data survive close/reopen. */}
      <Box
        id="unify-zoom-clinical-panel"
        pos="fixed"
        top={CHROME_HEADER_HEIGHT + PANEL_INSET}
        right={PANEL_INSET}
        bottom={PANEL_INSET}
        style={{
          width: PANEL_WIDTH,
          maxWidth: `calc(100% - ${PANEL_INSET * 2}px)`,
          zIndex: ZOOM_CHROME_Z_INDEX,
          boxShadow: panelOpen ? '0 16px 48px rgba(0, 0, 0, 0.45)' : undefined,
          borderRadius: 'var(--mantine-radius-sm)',
          overflow: 'hidden',
          pointerEvents: panelOpen ? 'auto' : 'none',
          backgroundColor: 'white',
          visibility: panelOpen ? 'visible' : 'hidden',
        }}
        aria-hidden={!panelOpen}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <SessionClinicalPanelContent
          appointmentId={appointmentId}
          onClose={() => {
            void closePanel();
          }}
          onRegisterFlush={registerFlush}
        />
      </Box>

      {/* Mantine Select dropdowns portal here so options stay above Zoom and receive clicks. */}
      <Box
        id="unify-zoom-combobox-portal"
        style={{
          position: 'fixed',
          top: CHROME_HEADER_HEIGHT,
          right: 0,
          width: PANEL_WIDTH + 48,
          bottom: 0,
          zIndex: ZOOM_OVERLAY_COMBOBOX_Z_INDEX,
          pointerEvents: 'none',
        }}
      />

      {/* Full-width dialogs (e.g. the session scheduler) portal here — the combobox
          container above is clipped to the sidebar. */}
      <Box
        id="unify-zoom-modal-portal"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: ZOOM_OVERLAY_MODAL_Z_INDEX,
          pointerEvents: 'none',
        }}
      />

      {!panelOpen ? (
        <Tooltip label="Open clinical sidebar" position="left" withArrow>
          <ActionIcon
            ref={openButtonRef}
            variant="filled"
            color="gold"
            size={EDGE_CONTROL_SIZE}
            radius="md"
            aria-label="Open clinical sidebar"
            onClick={(e) => {
              e.stopPropagation();
              openPanel();
            }}
            style={{
              position: 'fixed',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              zIndex: ZOOM_CHROME_Z_INDEX,
              pointerEvents: 'auto',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <IconLayoutSidebarRightExpand size={22} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Box>,
    document.body,
  );

  if (joinAllowed === false) {
    return (
      <Text c="dimmed" ta="center" mt="xl" maw={480} mx="auto" px="md">
        You are not a participant on this meeting, so the video session is not available.
      </Text>
    );
  }

  if (joinAllowed !== true) {
    return <></>;
  }

  if (error) {
    return <Text c="red" ta="center" mt="xl">{error}</Text>;
  }

  // Let Zoom Meeting SDK own the viewport; any React chrome here can block or obscure the SDK UI.
  if (isClientJoin) {
    return <></>;
  }

  return <>{overlay}</>;
}

async function startMeeting(
  medplum: ReturnType<typeof useMedplum>,
  cfg: {
    meetingNumber: string;
    password: string;
    userName: string;
    userEmail: string;
    role: number;
    clientJoin?: boolean;
  },
): Promise<ZoomClient> {
  const { ZoomMtg } = await import('@zoom/meetingsdk');
  ZoomMtg.preLoadWasm();
  ZoomMtg.prepareWebSDK();

  const root = document.getElementById('zmmtg-root');
  if (root) {
    root.style.display = 'block';
  }

  const signature = await getZoomSignature(medplum, cfg.meetingNumber, cfg.role);

  await initClient(ZoomMtg, cfg.clientJoin === true);
  await joinClient(ZoomMtg, cfg, signature);
  return ZoomMtg;
}

function leaveZoomMeeting(ZoomMtg: ZoomClient): Promise<void> {
  return new Promise((resolve) => {
    ZoomMtg.leaveMeeting({
      confirm: false,
      success: () => resolve(),
      error: () => resolve(),
    });
  });
}

function initClient(ZoomMtg: ZoomClient, clientJoin: boolean): Promise<void> {
  const leavePath = clientJoin ? '/client-portal/session/zoom-closed' : '/dashboard/session/zoom-closed';
  return new Promise((resolve, reject) => {
    ZoomMtg.init({
      leaveUrl: window.location.origin + leavePath,
      patchJsMedia: true,
      disableInvite: true,
      disableCallOut: true,
      disableRecord: true,
      disablePreview: true,
      disableZoomLogo: true,
      disablePictureInPicture: true,
      isSupportChat: false,
      isSupportQA: false,
      isSupportPolling: false,
      isSupportBreakout: false,
      meetingInfo: [],
      leaveOnPageUnload: true,
      success: () => resolve(),
      error: (err: unknown) => reject(err),
    });
  });
}

function joinClient(
  ZoomMtg: ZoomClient,
  cfg: { meetingNumber: string; password: string; userName: string; userEmail: string },
  signature: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    ZoomMtg.join({
      signature,
      meetingNumber: cfg.meetingNumber,
      passWord: cfg.password,
      userName: cfg.userName,
      userEmail: cfg.userEmail,
      success: () => resolve(),
      error: (err: unknown) => reject(err),
    });
  });
}
